<template>
  <div class="status-row-actions">
    <button
      v-show="enableReschedule"
      :disabled="isCompleteData ? true : undefined"
      class="btn -link linked-to-complete"
      @click="navigate(rescheduleUrl)"
    >
      Reschedule
    </button>

    <button
      :disabled="isCompleteData ? true : undefined"
      class="btn -link linked-to-complete"
      @click="openModal"
    >
      Remove
    </button>

    <scheduled-complete-button
      v-show="enableComplete"
      :is-complete="isCompleteData"
      :scheduled-id="id"
      :entity-id="entityId"
      :entity-type="'plan'"
      :entity-category="entityCategory"
      :disabled="completeActionsDisabled"
      @complete-updated="toggleComplete"
    />
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { mapState, mapActions } from 'pinia'
import { useModalsStore } from '@/stores/modals'
import { useCalendarStore } from '@/stores/calendar'
import { useUserAuthStore } from '@/stores/user-auth'
import ScheduledCompleteButton from '@/components/buttons/completes/scheduled-complete-button.vue'
import { fbApi } from '@/utils/http-api'

export default defineComponent({
  name: 'ProgramInstanceActions',

  components: { ScheduledCompleteButton },

  props: {
    id: {
      type: Number,
      required: true,
    },
    // Plan Id used for state.completes
    entityId: {
      type: Number,
      required: true,
    },
    enableComplete: {
      type: Boolean,
      required: true,
    },
    enableReschedule: {
      type: Boolean,
      required: true,
    },
    rescheduleUrl: {
      type: String,
      default: '',
    },
    isComplete: {
      type: Boolean,
      required: true,
    },
    isMealPlan: {
      type: Boolean,
      default: false,
    },
    entityCategory: {
      default: 'program',
      validator: (val) => ['program', 'challenge', 'routine'].includes(val),
    },
  },
  emits: ['deleted'],

  data() {
    return {
      isCompleteData: this.isComplete,
      deleting: false,
    }
  },

  computed: {
    ...mapState(useCalendarStore, ['completeActionsDisabled']),
    ...mapState(useUserAuthStore, ['user']),
    ...mapState(useModalsStore, ['simpleModal']),

    modalId() {
      return `program-instance-delete-modal-${this.id}`
    },

    currentUri() {
      return `${window.location.pathname}${window.location.search}`
    },

    deleteMessage() {
      // meal plan specific
      if (this.isMealPlan) {
        return 'Please note - completed recipes will be turned into individually scheduled recipes, and no progress will be lost as a result of removing the program from the calendar. Recipes not yet marked as completed will be removed.'
      }

      // default;
      return 'Please note - completed workouts will be turned into individually scheduled workouts, and no progress will be lost as a result of removing the program from the calendar. Workouts not yet marked as completed will be removed.'
    },
  },

  watch: {
    'simpleModal.action'(newAction, prevAction) {
      if (
        this.simpleModal.id === this.modalId &&
        newAction !== prevAction &&
        newAction === 'confirm' &&
        !this.deleting
      ) {
        this.deleteGrouping()
      }
    },
  },

  methods: {
    ...mapActions(useModalsStore, ['openSimpleModal']),

    toggleComplete(state) {
      this.isCompleteData = state
    },

    navigate(url) {
      window.location.href = url
    },

    openModal() {
      this.openSimpleModal({
        type: 'CONFIRM',
        id: this.modalId,
        props: {
          heading: 'Remove from Schedule',
          subheading: this.deleteMessage,
        },
      })
    },

    async deleteGrouping() {
      // disable double fetch
      if (this.deleting) {
        return
      }

      // reset
      this.deleting = true

      try {
        await fbApi.delete(`/calendar/programs/${this.id}`, {
          skipErrorModal: false,
        })

        this.$emit('deleted', this.id)
      } catch (e) {
        // empty
      } finally {
        this.deleting = false
      }
    },
  },
})
</script>
